import { extractInstanceIds, hasInstanceMarker, replaceInstanceMarkers } from './instanceMarker';

/** Every distinct instance id marked anywhere inside a value. */
export const collectInstanceIds = (value: unknown): string[] => {
  const ids: string[] = [];

  const walk = (node: unknown): void => {
    if (typeof node === 'string') {
      if (hasInstanceMarker(node)) ids.push(...extractInstanceIds(node));
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (isPlainObject(node)) {
      Object.values(node).forEach(walk);
    }
  };

  walk(value);
  return [...new Set(ids)];
};

/** Only plain objects are copied: a Date or a class instance would lose its prototype. */
const isPlainObject = (node: unknown): node is Record<string, unknown> => {
  if (node === null || typeof node !== 'object') return false;
  const prototype = Object.getPrototypeOf(node);
  return prototype === Object.prototype || prototype === null;
};

/**
 * The same value with every marker replaced by its label. Unchanged branches keep their
 * original reference: a chart handed a fresh `series` array replays its animation.
 */
export const resolveInstanceMarkers = <T>(value: T, labelOf: (id: string) => string): T => resolve(value, labelOf) as T;

const resolve = (node: unknown, labelOf: (id: string) => string): unknown => {
  if (typeof node === 'string') {
    if (!hasInstanceMarker(node)) return node;
    const resolved = replaceInstanceMarkers(node, labelOf);
    return resolved === node ? node : resolved;
  }

  if (Array.isArray(node)) {
    let changed = false;
    const next = node.map((item) => {
      const resolved = resolve(item, labelOf);
      if (resolved !== item) changed = true;
      return resolved;
    });
    return changed ? next : node;
  }

  if (isPlainObject(node)) {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(node)) {
      const resolved = resolve(item, labelOf);
      if (resolved !== item) changed = true;
      next[key] = resolved;
    }
    return changed ? next : node;
  }

  return node;
};
